import React, { useState } from 'react';
import { SiteVisitTableView } from '../components/SiteVisitTableView';
import { UpdateSiteVisitForm } from '../modals/UpdateSiteVisitForm';
import { ViewSiteVisitModal } from '../modals/ViewSiteVisitModal';
import { useSiteVisits } from '../hooks/useSiteVisits';
import { ConfirmationModal } from '@/components/ui';
import { showSuccessMessage, showApiError } from '@/lib/toast';

import { SiteVisitControlPanel } from '../components/SiteVisitControlPanel';
import { useDebounce } from '@/hooks/useDebounce';
import { exportSiteVisitData } from '@/utils/SiteVisit';
import { getCumulativeStatuses } from '@/constants/globalConstants';

export const SiteVisitScreen = ({ 
  filters: globalFilters = {}, 
  onLeadClick 
}) => {
  const [localSearch, setLocalSearch] = useState('');
  const debouncedSearch = useDebounce(localSearch, 500);
  
  const [localFilters, setLocalFilters] = useState({
    status: getCumulativeStatuses('site_visit'),
    date: 'all',
    fromDate: '',
    toDate: ''
  });

  const combinedFilters = {
    ...globalFilters,
    ...localFilters,
    search: debouncedSearch
  };

  const { 
    siteVisits, 
    isLoading, 
    updateSiteVisit, 
    deleteSiteVisit,
    isDeleting 
  } = useSiteVisits(combinedFilters);

  const handleFilterChange = (key, value) => {
    setLocalFilters(prev => ({ ...prev, [key]: value }));
  };

  const [isExportingData, setIsExportingData] = useState(false);

  const handleExport = async () => {
    try {
      setIsExportingData(true);
      exportSiteVisitData(siteVisits);
      showSuccessMessage("Site Visits exported successfully");
    } catch (e) {
      showApiError(e);
    } finally {
      setIsExportingData(false);
    }
  };

  const [selectedSiteVisit, setSelectedSiteVisit] = useState(null);
  const [isUpdateFormOpen, setIsUpdateFormOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [confirmState, setConfirmState] = useState({ isOpen: false });

  const handleEditClick = (visit) => {
    setSelectedSiteVisit(visit);
    setIsUpdateFormOpen(true);
  };

  const handleViewClick = (visit) => {
    setSelectedSiteVisit(visit);
    setIsViewModalOpen(true);
  };

  const handleUpdateSubmit = async (id, data) => {
    try {
      await updateSiteVisit({ id, data });
      showSuccessMessage("Site Visit updated successfully");
      setIsUpdateFormOpen(false);
      setSelectedSiteVisit(null);
    } catch (e) {
      showApiError(e);
      throw e; 
    }
  };

  const handleDeleteClick = (visit) => {
    setSelectedSiteVisit(visit);
    setConfirmState({
      isOpen: true,
      title: "Delete Site Visit",
      message: `Are you sure you want to permanently delete all Site Visit data and photos for ${visit.customerName || visit.firstName}? The lead will be moved back to the Appointment Scheduled stage.`,
      onConfirm: async () => {
        try {
          setConfirmState(prev => ({ ...prev, loading: true }));
          await deleteSiteVisit(visit._id);
          showSuccessMessage("Site Visit deleted successfully");
          setConfirmState(prev => ({ ...prev, isOpen: false }));
        } catch (e) {
          showApiError(e);
        } finally {
          setConfirmState(prev => ({ ...prev, loading: false }));
        }
      }
    });
  };

  return (
    <div className="block">
      {/* <SiteVisitControlPanel
        search={localSearch}
        setSearch={setLocalSearch}
        filters={localFilters}
        setFilters={handleFilterChange}
        onExport={handleExport}
        isExporting={isExportingData}
      /> */}
      
      <SiteVisitTableView
        siteVisits={siteVisits}
        isLoading={isLoading}
        onSiteVisitClick={(visit) => {
          if (onLeadClick) onLeadClick(visit);
        }}
        onViewClick={handleViewClick}
        onEditClick={handleEditClick}
        onDeleteClick={handleDeleteClick}
      />

      {isUpdateFormOpen && (
        <UpdateSiteVisitForm
          isOpen={isUpdateFormOpen}
          onClose={() => {
            setIsUpdateFormOpen(false);
            setSelectedSiteVisit(null);
          }}
          initialData={selectedSiteVisit}
          onSubmit={handleUpdateSubmit}
        />
      )}

      {isViewModalOpen && (
        <ViewSiteVisitModal
          isOpen={isViewModalOpen}
          onClose={() => {
            setIsViewModalOpen(false);
            setSelectedSiteVisit(null);
          }}
          lead={selectedSiteVisit}
        />
      )}

      {confirmState.isOpen && (
        <ConfirmationModal
          isOpen={confirmState.isOpen}
          onClose={() => setConfirmState({ isOpen: false })}
          title={confirmState.title}
          message={confirmState.message}
          onConfirm={confirmState.onConfirm}
          variant={confirmState.variant || "danger"}
          confirmText={confirmState.confirmText || "Confirm"}
          loading={confirmState.loading || isDeleting}
        />
      )}
    </div>
  );
};
